import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker - With One-Time Purchase Option
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    options: { type: Object },

    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        width: 100%;
      }

      .skio-plan-picker {
        display: flex;
        padding: 0px;
        margin: 0px;
        border: 0px; 
        display: flex;
        flex-direction: column;
        font-size: 14px;
        color: black;
        margin-bottom: 1rem;
        font-family: inherit;
        width: auto;
        font-size: 14px
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .subscription-option {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: 0.7px solid #D9D9D9;
        background-color: white;
        color: #000;
        cursor: pointer;
      }

      .onetime-option {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: 0.7px solid #D9D9D9;
        border-top: 0;
        background-color: white;
        color: #000;
        cursor: pointer;
      }

      .subscription-option--selected {
        background-color: var(--skio-group-background-selected-color, #FFECF8);
      }

      .onetime-option--selected {
        background-color: var(--skio-group-background-selected-color, #FFECF8);
      }

      .subscription-input, .onetime-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .subscription-input:focus-visible ~ .subscription-label,
      .onetime-input:focus-visible ~ .onetime-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .subscription-label, .onetime-label {
        display: flex;
        align-items: start;
        justify-content: space-between;
        padding: 16px;
        cursor: pointer;
        gap: 12px;
      }

      .subscription-option--selected .subscription-label,
      .onetime-option--selected .onetime-label {
        font-weight: 700;
      }

      .skio-widget__image {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .subscription-input:not(:checked) + .subscription-label .skio-widget__checked-icon,
      .onetime-input:not(:checked) + .onetime-label .skio-widget__checked-icon {
        display: none;
      }

      .subscription-input:checked + .subscription-label .skio-widget__unchecked-icon,
      .onetime-input:checked + .onetime-label .skio-widget__unchecked-icon {
        display: none;
      }

      .subscription-text {
        display: flex;
        align-items: start;
        flex-direction: column;
        gap: 4px;
      }

      .onetime-text {
        display: flex;
        align-items: start;
        flex-direction: column;
        gap: 4px;
        padding-right: 50px;
      }

      .subscription-title, .onetime-title {
        font-size: 14px;
        line-height: 1.4;
        font-weight: 700;
      }

      .subscription-subtitle, .onetime-subtitle {
        font-size: 12px;
        color: #00000080;
        font-weight: 500;
      }

      .subscription-test {
        font-weight: 500;
        font-size: 12px;
      }

      .subscription-pricing, .onetime-pricing {
        display: flex;
        align-items: start;
        font-size: 14px;
        font-weight: 700;
      }

      .savings {
        color: white;
        font-size: 14px;
        padding: 2px 6px;
        background-color: #8F0000;
        font-weight: 700;
        margin-left: 2px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        align-items: start;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }

      .minimum-notice {
        color: #ae3b40;
        text-transform: uppercase;
        font-size: 11px;
        font-weight: 600;
        margin-top: 16px;
        text-align: center;
      }
    `,
  ]

  constructor() {
    super()

    // Start - Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables

    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted')
    
    // Debug money format
    console.log('Money format on mount:', this.moneyFormat);
    console.log('Shopify money format:', window?.Shopify?.money_format);

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  _getSellingPlanAllocation(variant, sellingPlanId) {
    if (!variant) {
        console.error(`Skio: Could not find variant ID`);
        return null;
    }

    // Debug the variant itself
    console.log('Variant price (should be 5800 for $58):', variant.price);
    console.log('Variant compare_at_price:', variant.compare_at_price);
    console.log('All selling plan allocations:', variant.selling_plan_allocations);

    let allocation = variant.selling_plan_allocations.find(function (plan) {
        return `${plan.selling_plan_id}` == sellingPlanId;
    });
    
    // Debug logging - show the full allocation object
    console.log('Full allocation object:', JSON.stringify(allocation, null, 2));
    
    // Use the selling plan allocation price directly
    let price = allocation?.price || allocation?.per_delivery_price;
    console.log('Final price before formatting:', price);
    
    return this.money(price);
  }

  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker - With One-Time Purchase
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0) {
      return this.debug ? this.setupMode() : ''
    }

    // Debug pricing at render time
    console.log('=== RENDER DEBUG ===');
    console.log('Selected variant price:', this.selectedVariant?.price);
    console.log('Selected variant compare_at_price:', this.selectedVariant?.compare_at_price);
    console.log('Money format:', this.moneyFormat);
    console.log('Test money formatting - 5800:', this.money(5800));
    console.log('Test money formatting - 4800:', this.money(4800));

    // Get all available selling plans from all groups
    const allSellingPlans = this.availableSellingPlanGroups?.length 
      ? this.availableSellingPlanGroups.flatMap(group => this.getAvailableSellingPlans(group))
      : []

    // Check if one-time purchase should be disabled based on product tags
    const disableOneTime = this.product?.tags?.includes('subscription-only');
    
    console.log('Product tags:', this.product?.tags);
    console.log('Disable one-time purchase:', disableOneTime);

    return html`
      ${this.debug ? this.setupMode() : null}
      
      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend">
        
        </legend>
        
        ${allSellingPlans.map((sellingPlan, index) => html`
          <div class="subscription-option ${this.selectedSellingPlan?.id === sellingPlan.id ? 'subscription-option--selected' : ''}">
            <input
              id="subscription-${index}-${this.key}"
              class="subscription-input"
              name="skio-plan-${this.key}"
              type="radio"
              value="${sellingPlan.id}"
              ?checked=${this.selectedSellingPlan?.id === sellingPlan.id}
              @change=${() => this.selectSellingPlan(sellingPlan.id)}
            />
            
            <label class="subscription-label" for="subscription-${index}-${this.key}">

              <svg class="skio-widget__checked-icon skio-widget__image" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="7" fill="#8F0000"/>
              <circle cx="7" cy="7" r="4" fill="white"/>
              </svg>

              <svg class="skio-widget__unchecked-icon skio-widget__image" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="6.5" stroke="#B0B0B0"/>
              </svg>
              
              <div class="subscription-text">
                <div class="subscription-title">${sellingPlan.name}
                ${this.discountText(sellingPlan) ? html`
                  <span class="savings">Save ${this.discountText(sellingPlan)}</span>
                ` : ''}
                </div>
                <div class="subscription-subtitle">Free shipping. 3 month minimum.</div>
              </div>
              
              <div class="subscription-pricing">
                <span>${this._getSellingPlanAllocation(this.selectedVariant, sellingPlan.id)}</span>
                
              </div>
            </label>
          </div>
        `)}

        <!-- One-time purchase option - only show if not disabled by tag -->
        ${!disableOneTime ? html`
          <div class="onetime-option ${!this.selectedSellingPlan ? 'onetime-option--selected' : ''}">
            <input
              id="onetime-${this.key}"
              class="onetime-input"
              name="skio-plan-${this.key}"
              type="radio"
              value="onetime"
              ?checked=${!this.selectedSellingPlan}
              @change=${() => this.selectOneTime()}
            />
            
            <label class="onetime-label" for="onetime-${this.key}">

              <svg class="skio-widget__checked-icon skio-widget__image" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="7" fill="#8F0000"/>
              <circle cx="7" cy="7" r="4" fill="white"/>
              </svg>

              <svg class="skio-widget__unchecked-icon skio-widget__image" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="6.5" stroke="#B0B0B0"/>
              </svg>
              
              <div class="onetime-text">
                <div class="onetime-title">One-time purchase</div>
                <div class="onetime-subtitle">No commitment. Purchase once.</div>
              </div>
              
              <div class="onetime-pricing">
                <span>${this.money(this.selectedVariant.price)}</span>
              </div>
            </label>
          </div>
        ` : ''}
      </fieldset>
    `
  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  bindFormEvents() {
    this.form = document.querySelector(`#${this.options?.form_id}`) || this.closest('form[action*="/cart/add"]')

    if (!this.form) {
      return
    }

    this.variantInput = this.form.querySelector('[name="id"]')

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
      })
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')

    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
      // Debug the entire product and variant data
      console.log('Full product object:', this.product);
      console.log('Selected variant:', this.selectedVariant);
      console.log('Selected variant price:', this.selectedVariant?.price);
      console.log('Selected variant compare_at_price:', this.selectedVariant?.compare_at_price);
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }

      // Check if one-time purchase should be disabled based on product tags
      const disableOneTime = this.product?.tags?.includes('subscription-only');

      // Auto-select first subscription option by default, or force selection if one-time is disabled
      if (this.availableSellingPlanGroups?.length && (!this.selectedSellingPlan || disableOneTime)) {
        const allSellingPlans = this.availableSellingPlanGroups.flatMap(group => this.getAvailableSellingPlans(group))
        
        if (allSellingPlans.length > 0) {
          // If one-time is disabled, always select the first subscription plan
          // Otherwise, select the first available selling plan by default
          this.selectSellingPlan(allSellingPlans[0].id)
        }
      }
    }

    if (changed.has('selectedSellingPlan')) {
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.selectedSellingPlan ? this.price(this.selectedSellingPlan) : this.money(this.selectedVariant.price)
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  selectOneTime() {
    this.selectedSellingPlanGroup = null
    this.selectedSellingPlan = null
  }

  updateExternalPrice() {
    document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
      this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
    })
  }

  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return ''
    } else {
      return this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%'
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] && parts[1] !== '00' ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  price(selling_plan, formatted = true) {
    // Use the selling plan allocation price directly instead of calculating discounts
    if (!selling_plan) {
      console.log('No selling plan, using variant price:', this.selectedVariant.price);
      return formatted ? this.money(this.selectedVariant.price) : this.selectedVariant.price;
    }
    
    const allocation = this.selectedVariant.selling_plan_allocations.find(
      allocation => allocation.selling_plan_id === selling_plan.id
    );
    
    console.log('Found allocation for price method:', allocation);
    
    const price = allocation?.price || allocation?.per_delivery_price || this.selectedVariant.price;
    console.log('Final price in price method:', price);
    
    return formatted ? this.money(price) : price;
  }

  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker